import { useEffect } from 'react';

import { Cents, Money, formatMoney } from '@betterment/core';
import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useFlowState } from '@betterment/js-runtime';
import {
  Box,
  Button,
  CenteredLayout,
  H1,
  IconButton,
  PancakeStack,
  Separator,
  Text,
  WorkflowHeader,
} from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ErrorBoundary } from '@betterment/shared-components';
import { useFeatureFlag } from '@betterment/shared-components/src/components/feature-flag/useFeatureFlag';
import {
  sharesFromMoneyTradeEstimate,
  sharesTradeEstimateCopy,
} from '@betterment/shared-components/src/utils/tradeEstimateHelpers';
import { Form, Formik, FormikProps } from 'formik';
import { useNavigate } from 'react-router-dom';

import { EstimatedTradeTimingDialog } from './EstimatedTradeTimingDialog';
import { WashSaleDetectedDialog } from './WashSaleDetectedDialog';

import { TaxImpactUnavailable } from '../../direct-trading-accounts/shared/TaxImpactUnavailable';
import { BUY_ORDER_COPY } from '../../direct-trading-accounts/shared/TaxImpactUnavailableCopy';
import {
  EstimatedTradeTiming,
  FinancialSecurityOrderRequestDirection,
  TaxImpact as GraphQLTaxImpact,
} from '../../graphql/schema';
import { WithLayout } from '../../RetailApp/components/WithLayout';
import { locationAssign } from '../../Shared/path-helpers';
import { DirectTradingAccountBuyDetailsDocument } from '../graphql/directTradingAccountBuyDetails.generated';
import { CreateFinancialSecurityOrderRequestDocument } from '../graphql/mutation.createFinancialSecurityOrderRequest.generated';

export type ReviewOrderProps = {
  accountId: string;
  securityId: string;
};

type ReviewValues = {
  order_amount_cents?: Cents;
};

type FlowState = ReviewValues & {
  estimated_trade_timing?: EstimatedTradeTiming;
  tax_impact_preview?: GraphQLTaxImpact;
  order_will_be_cancelable?: boolean;
};

export type ReviewStepProps = ReviewOrderProps & StepProps;

export const ReviewStep: React.FC<ReviewStepProps> = ({ goToNextStep, goToStep, accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountBuyDetailsDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  const { flowState } = useFlowState<FlowState>();
  const navigate = useNavigate();
  const { enabled: isAfterHoursCancellationEnabled } = useFeatureFlag({
    splitName: 'retail.self_directed_after_hours_order_cancellation_q4_2025_enabled',
    context: 'buy_order_review_step',
  });

  const [createFinancialSecurityOrderRequest, { loading: isMutationLoading }] = useGraphQLMutation(
    CreateFinancialSecurityOrderRequestDocument
  );

  useEffect(() => {
    if (!flowState.values.order_amount_cents) {
      goToStep('form');
    }
  }, [flowState.values.order_amount_cents, goToStep]);

  if (!data) return null;

  const { account } = data;
  const { financialSecurity } = account;
  const price = financialSecurity.statistics.latestPrice.amount;

  const handleSubmit = async (values: ReviewValues) => {
    if (!values.order_amount_cents) {
      return;
    }

    try {
      await createFinancialSecurityOrderRequest({
        variables: {
          input: {
            accountId: { directTradingAccountId: data.account.id },
            financialSecurityId: securityId,
            direction: FinancialSecurityOrderRequestDirection.Buy,
            value: { amount: Money(values.order_amount_cents) },
            sellAll: false,
            estimatedShares: sharesFromMoneyTradeEstimate(Money(values.order_amount_cents || 0), price),
            sharePriceAtRequest: price,
          },
        },
      });

      goToNextStep();
    } catch (error) {
      void navigate('/generic_error');
    }
  };

  const initialValues: ReviewValues = {
    order_amount_cents: flowState.values.order_amount_cents,
  };

  const estimatedTradeTiming = flowState.values.estimated_trade_timing;
  const taxImpactPreview = flowState.values.tax_impact_preview;
  const willOrderBeCancelable = flowState.values.order_will_be_cancelable;

  return (
    <ErrorBoundary>
      <WithLayout parts={{ sidebar: false, footer: true }}>
        <PancakeStack css={{ maxWidth: '100%', bg: 'background.surface' }}>
          <PancakeStack.Header
            css={{
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'center',
              borderBottomStyle: 'solid',
              borderBottomWidth: 'thin',
              borderColor: 'border',
            }}
          >
            <WorkflowHeader css={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <WorkflowHeader.Leading>
                <IconButton
                  name="LeftArrowSmall"
                  label="Back"
                  spacing="compact"
                  onClick={() => goToStep('form', { order_amount_cents: flowState.values.order_amount_cents })}
                />
              </WorkflowHeader.Leading>
              <WorkflowHeader.Title />
              <WorkflowHeader.Trailing>
                <IconButton
                  name="X"
                  label="Close"
                  spacing="compact"
                  onClick={() => locationAssign(flowState.returnToPath)}
                />
              </WorkflowHeader.Trailing>
            </WorkflowHeader>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ paddingRight: 'space.200', paddingLeft: 'space.200' }}>
            <CenteredLayout size="medium" css={{ paddingTop: 'space.500' }}>
              <H1 css={{ marginBottom: 'space.200' }}>Review {financialSecurity.symbol} buy</H1>
              {!isAfterHoursCancellationEnabled && (
                <Text css={{ marginBottom: 'space.300' }}>Submitted orders can't be canceled or edited.</Text>
              )}
              <Formik onSubmit={handleSubmit} initialValues={initialValues}>
                {(formikProps: FormikProps<ReviewValues>) => (
                  <Form className={css({ width: '100%' })}>
                    <Box
                      css={{
                        width: '100%',
                        borderRadius: 'medium',
                        border: 'token(borderWidths.thin) solid token(colors.border)',
                        padding: 'space.300',
                      }}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box css={{ alignSelf: 'center' }}>
                          <Text>Security</Text>
                        </Box>
                        <Box css={{ display: 'flex', flexDirection: 'column', textAlign: 'end' }}>
                          <Text css={{ alignSelf: 'end' }}>{financialSecurity.name}</Text>
                          <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                            {financialSecurity.symbol}
                          </Text>
                        </Box>
                      </Box>
                      <Separator
                        css={{
                          marginY: 'space.300',
                        }}
                      />
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box css={{ alignSelf: 'center' }}>
                          <Text>Amount</Text>
                        </Box>
                        <Box css={{ display: 'flex', flexDirection: 'column', textAlign: 'end' }}>
                          <Text css={{ alignSelf: 'end' }}>
                            {formikProps.values.order_amount_cents &&
                              formatMoney(Money(formikProps.values.order_amount_cents))}
                          </Text>
                          <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                            {sharesTradeEstimateCopy(Money(formikProps.values.order_amount_cents || 0), price)}
                          </Text>
                        </Box>
                      </Box>
                      {taxImpactPreview && (
                        <>
                          <Separator
                            css={{
                              marginY: 'space.300',
                            }}
                          />
                          <WashSaleDetectedDialog
                            netWashedLosses={taxImpactPreview.netWashedLosses}
                            explanationHtml={taxImpactPreview.copy.buyOnlyWashSaleExplanation}
                          />
                        </>
                      )}
                      {!taxImpactPreview && (
                        <>
                          <Separator
                            css={{
                              marginY: 'space.300',
                            }}
                          />
                          <TaxImpactUnavailable header={BUY_ORDER_COPY.header} body={BUY_ORDER_COPY.body} />
                        </>
                      )}
                      <Separator
                        css={{
                          marginY: 'space.300',
                        }}
                      />
                      {estimatedTradeTiming && (
                        <EstimatedTradeTimingDialog estimatedTradeTiming={estimatedTradeTiming} />
                      )}
                    </Box>
                    {isAfterHoursCancellationEnabled && willOrderBeCancelable && (
                      <Box css={{ marginTop: 'space.200' }}>
                        <Text>Orders can be canceled outside of market hours.</Text>
                      </Box>
                    )}
                    <Button
                      css={{
                        marginTop: 'space.300',
                        px: 'space.600',
                      }}
                      type="submit"
                      variant="solidPrimary"
                      loading={isMutationLoading}
                    >
                      Submit
                    </Button>
                  </Form>
                )}
              </Formik>
            </CenteredLayout>
          </PancakeStack.Main>
        </PancakeStack>
      </WithLayout>
    </ErrorBoundary>
  );
};
